/**
 * HYPSEYA — Somatic Body Map
 * Where in your body does emotion live?
 * Interactive SVG body silhouette — tap zones, build heat map over 30 days.
 * Backed by /api/somatic-checkin (existing endpoint).
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BodySenseIcon, HeatIcon } from '../components/HypIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Body zones ──────────────────────────────────────────────────────────────
// Each zone has: id, label, svg path, centroid for label placement
const ZONES = [
  { id: 'head',          label: 'Head / Mind',     centroid: [100, 38] },
  { id: 'throat',        label: 'Throat',           centroid: [100, 80] },
  { id: 'chest',         label: 'Chest / Heart',    centroid: [100, 118] },
  { id: 'solar_plexus',  label: 'Solar Plexus',     centroid: [100, 148] },
  { id: 'belly',         label: 'Belly / Gut',      centroid: [100, 175] },
  { id: 'pelvis',        label: 'Pelvis / Root',    centroid: [100, 205] },
  { id: 'left_shoulder', label: 'Left Shoulder',    centroid: [145, 108] },
  { id: 'right_shoulder',label: 'Right Shoulder',   centroid: [55, 108]  },
  { id: 'left_arm',      label: 'Left Arm',         centroid: [152, 160] },
  { id: 'right_arm',     label: 'Right Arm',        centroid: [48, 160]  },
  { id: 'left_hand',     label: 'Left Hand',        centroid: [158, 210] },
  { id: 'right_hand',    label: 'Right Hand',       centroid: [42, 210]  },
  { id: 'upper_back',    label: 'Upper Back',       centroid: [100, 118] },
  { id: 'lower_back',    label: 'Lower Back',       centroid: [100, 175] },
  { id: 'left_hip',      label: 'Left Hip',         centroid: [120, 220] },
  { id: 'right_hip',     label: 'Right Hip',        centroid: [80, 220]  },
  { id: 'left_leg',      label: 'Left Leg',         centroid: [118, 270] },
  { id: 'right_leg',     label: 'Right Leg',        centroid: [82, 270]  },
  { id: 'feet',          label: 'Feet',             centroid: [100, 330] },
];

// SVG paths for body zones — simplified body silhouette (200×350 viewBox)
const ZONE_PATHS = {
  head:           'M100,10 C120,10 135,25 135,48 C135,70 120,78 100,78 C80,78 65,70 65,48 C65,25 80,10 100,10Z',
  throat:         'M85,78 L85,95 Q100,100 115,95 L115,78 Q100,73 85,78Z',
  chest:          'M68,95 L68,145 L132,145 L132,95 Q115,90 100,90 Q85,90 68,95Z',
  solar_plexus:   'M68,145 L68,165 L132,165 L132,145Z',
  belly:          'M68,165 L68,195 L132,195 L132,165Z',
  pelvis:         'M68,195 L72,220 L128,220 L132,195Z',
  left_shoulder:  'M132,95 L155,90 L162,120 L132,125 L132,95Z',
  right_shoulder: 'M68,95 L45,90 L38,120 L68,125 L68,95Z',
  left_arm:       'M132,125 L162,120 L165,195 L135,195Z',
  right_arm:      'M68,125 L38,120 L35,195 L65,195Z',
  left_hand:      'M135,195 L165,195 L163,220 L137,220Z',
  right_hand:     'M65,195 L35,195 L37,220 L63,220Z',
  left_hip:       'M100,220 L128,220 L125,245 L103,245Z',
  right_hip:      'M100,220 L72,220 L75,245 L97,245Z',
  left_leg:       'M103,245 L125,245 L120,315 L105,315Z',
  right_leg:      'M97,245 L75,245 L80,315 L95,315Z',
  feet:           'M80,315 L120,315 L122,335 L78,335Z',
};

// Emotions that can be associated with a zone tap
const EMOTIONS = [
  { id: 'tension',   label: 'Tension',    color: '#A33B2B', emoji: '🔴' },
  { id: 'anxiety',   label: 'Anxiety',    color: '#B8906A', emoji: '🟠' },
  { id: 'sadness',   label: 'Sadness',    color: '#4A7B9D', emoji: '🔵' },
  { id: 'numbness',  label: 'Numbness',   color: '#8B9B97', emoji: '⚪' },
  { id: 'warmth',    label: 'Warmth',     color: '#7BAA72', emoji: '🟢' },
  { id: 'openness',  label: 'Openness',   color: '#2D8B87', emoji: '✦'  },
  { id: 'heaviness', label: 'Heaviness',  color: '#5A4A6B', emoji: '🟣' },
];

const EMOTION_COLOR = Object.fromEntries(EMOTIONS.map(e => [e.id, e.color]));

// ── Heat map color from count ─────────────────────────────────────────────────
function heatColor(count, maxCount) {
  if (!count || !maxCount) return 'transparent';
  const intensity = Math.min(1, count / maxCount);
  // Teal at low → warm amber/red at high
  const r = Math.round(30 + intensity * 163);
  const g = Math.round(107 - intensity * 60);
  const b = Math.round(104 - intensity * 80);
  return `rgba(${r},${g},${b},${0.15 + intensity * 0.45})`;
}

// ── Body SVG ──────────────────────────────────────────────────────────────────
function BodySVG({ heatData, maxCount, selected, onZoneClick, showHeat }) {
  return (
    <svg viewBox="0 0 200 350" style={{ width: '100%', maxWidth: '220px', cursor: 'pointer', userSelect: 'none' }}>
      {/* Background body shape (grey silhouette) */}
      <ellipse cx="100" cy="48" rx="35" ry="38" fill="#E8EFEE" />
      <rect x="68" y="85" width="64" height="140" rx="8" fill="#E8EFEE" />
      <rect x="35" y="88" width="34" height="130" rx="10" fill="#E8EFEE" />
      <rect x="131" y="88" width="34" height="130" rx="10" fill="#E8EFEE" />
      <rect x="72" y="220" width="26" height="115" rx="8" fill="#E8EFEE" />
      <rect x="102" y="220" width="26" height="115" rx="8" fill="#E8EFEE" />

      {/* Clickable zones */}
      {Object.entries(ZONE_PATHS).map(([zoneId, path]) => {
        const count = heatData[zoneId] || 0;
        const isSelected = selected === zoneId;
        const fill = showHeat ? heatColor(count, maxCount) : (isSelected ? 'rgba(45,139,135,0.30)' : 'transparent');
        return (
          <path
            key={zoneId}
            d={path}
            fill={fill}
            stroke={isSelected ? '#1E6B68' : 'transparent'}
            strokeWidth={isSelected ? '2' : '0'}
            style={{ transition: 'fill 0.25s ease' }}
            onClick={() => onZoneClick(zoneId)}
          >
            <title>{ZONES.find(z => z.id === zoneId)?.label || zoneId}</title>
          </path>
        );
      })}

      {/* Body outline strokes for clarity */}
      <ellipse cx="100" cy="48" rx="35" ry="38" fill="none" stroke="rgba(30,107,104,0.12)" strokeWidth="1.5" />
      <line x1="100" y1="78" x2="100" y2="86" stroke="rgba(30,107,104,0.12)" strokeWidth="1.5" />
      <rect x="68" y="85" width="64" height="140" rx="8" fill="none" stroke="rgba(30,107,104,0.12)" strokeWidth="1.5" />
      <rect x="35" y="88" width="34" height="130" rx="10" fill="none" stroke="rgba(30,107,104,0.12)" strokeWidth="1.5" />
      <rect x="131" y="88" width="34" height="130" rx="10" fill="none" stroke="rgba(30,107,104,0.12)" strokeWidth="1.5" />
      <rect x="72" y="220" width="26" height="115" rx="8" fill="none" stroke="rgba(30,107,104,0.12)" strokeWidth="1.5" />
      <rect x="102" y="220" width="26" height="115" rx="8" fill="none" stroke="rgba(30,107,104,0.12)" strokeWidth="1.5" />

      {/* Heat count labels */}
      {showHeat && Object.entries(heatData).map(([zoneId, count]) => {
        if (!count) return null;
        const zone = ZONES.find(z => z.id === zoneId);
        if (!zone) return null;
        return (
          <text key={zoneId} x={zone.centroid[0]} y={zone.centroid[1]}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="9" fontWeight="700" fill="rgba(30,70,68,0.75)">
            {count}
          </text>
        );
      })}
    </svg>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SomaticMap() {
  const navigate = useNavigate();
  const [selectedZone, setSelectedZone]   = useState(null);
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [intensity, setIntensity]         = useState(5);
  const [note, setNote]                   = useState('');
  const [saving, setSaving]               = useState(false);
  const [saved, setSaved]                 = useState(false);
  const [history, setHistory]             = useState([]);
  const [showHeat, setShowHeat]           = useState(true);
  const [loading, setLoading]             = useState(true);

  const [userId] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').user_id; } catch { return null; }
  });

  // Build heat data from history
  const heatData = {};
  history.forEach(h => {
    const z = h.body_zone || h.zone;
    if (z) heatData[z] = (heatData[z] || 0) + 1;
  });
  const maxCount = Math.max(1, ...Object.values(heatData));

  useEffect(() => {
    if (userId) fetchHistory();
  }, [userId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/somatic-checkins/${userId}?limit=60`);
      setHistory(res.data.checkins || res.data || []);
    } catch (e) {}
    setLoading(false);
  };

  const handleZoneClick = (zoneId) => {
    setSelectedZone(prev => prev === zoneId ? null : zoneId);
    setSelectedEmotion(null);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!selectedZone || !selectedEmotion || !userId) return;
    setSaving(true);
    try {
      await axios.post(`${API}/somatic-checkin`, {
        user_id: userId,
        body_zone: selectedZone,
        emotion: selectedEmotion,
        intensity,
        note: note.trim() || null,
        created_at: new Date().toISOString(),
      });
      setSaved(true);
      setSelectedZone(null);
      setSelectedEmotion(null);
      setIntensity(5);
      setNote('');
      fetchHistory();
    } catch (e) {}
    setSaving(false);
  };

  const zoneName = ZONES.find(z => z.id === selectedZone)?.label || '';

  // Recent entries (last 7 days)
  const recent = history.slice(0, 7);

  // Most activated zones (top 3)
  const topZones = Object.entries(heatData)
    .sort(([,a],[,b]) => b - a)
    .slice(0, 3)
    .map(([z, c]) => ({ zone: z, count: c, label: ZONES.find(x => x.id === z)?.label || z }));

  const TEAL  = '#2D8B87';
  const DARK  = '#1E6B68';
  const CREAM = 'rgba(247,243,236,0.97)';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #EEF5F4 0%, #F5EFE8 60%, #EBE4DA 100%)',
      padding: '0 0 80px',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${DARK}, ${TEAL})`,
        padding: '56px 24px 40px',
        marginBottom: '28px',
      }}>
        <div style={{ maxWidth: '540px', margin: '0 auto' }}>
          <button onClick={() => navigate('/insights')} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '10px',
            color: '#fff', padding: '8px 14px', fontSize: '13px',
            cursor: 'pointer', marginBottom: '24px',
          }}>← Insights</button>
          <div style={{
            display: 'inline-block', background: 'rgba(255,255,255,0.15)',
            borderRadius: '20px', padding: '3px 12px', fontSize: '11px',
            fontWeight: 700, color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px',
          }}>Somatic Map</div>
          <h1 style={{ margin: '8px 0 6px', fontSize: '28px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
            Where do you feel it?
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.6 }}>
            Tap a body zone after each session. Over time, your personal somatic map emerges.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '540px', margin: '0 auto', padding: '0 20px' }}>

        {/* Heat map toggle */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button onClick={() => setShowHeat(!showHeat)} style={{
            background: showHeat ? 'rgba(45,139,135,0.12)' : 'rgba(0,0,0,0.05)',
            border: '1px solid rgba(45,139,135,0.20)',
            borderRadius: '10px', padding: '6px 14px',
            fontSize: '12px', fontWeight: 600, color: DARK, cursor: 'pointer',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <HeatIcon size={13} color={DARK} />
              {showHeat ? 'Heat map on' : 'Heat map off'}
            </span>
          </button>
        </div>

        {/* Body + zone picker side by side */}
        <div style={{
          background: CREAM, border: '1px solid rgba(30,107,104,0.10)',
          borderRadius: '24px', padding: '28px', marginBottom: '20px',
          boxShadow: '0 2px 16px rgba(30,107,104,0.07)',
          display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap',
        }}>
          {/* SVG body */}
          <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <BodySVG
              heatData={heatData}
              maxCount={maxCount}
              selected={selectedZone}
              onZoneClick={handleZoneClick}
              showHeat={showHeat}
            />
            <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#8B9B97', textAlign: 'center' }}>
              Tap a zone to check in
            </p>
          </div>

          {/* Zone list (scrollable alternative for accessibility) */}
          <div style={{ flex: 1, minWidth: '160px' }}>
            <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 600, color: '#6B8B87', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Or select zone
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {ZONES.filter((_, i) => i < 10).map(z => (
                <button key={z.id} onClick={() => handleZoneClick(z.id)} style={{
                  textAlign: 'left', padding: '6px 12px',
                  background: selectedZone === z.id ? 'rgba(30,107,104,0.12)' : 'transparent',
                  border: `1px solid ${selectedZone === z.id ? 'rgba(30,107,104,0.35)' : 'rgba(0,0,0,0.07)'}`,
                  borderRadius: '8px', fontSize: '13px', color: selectedZone === z.id ? DARK : '#4A6A66',
                  fontWeight: selectedZone === z.id ? 600 : 400,
                  cursor: 'pointer', transition: 'all 0.15s',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span>{z.label}</span>
                  {heatData[z.id] > 0 && (
                    <span style={{ fontSize: '10px', fontWeight: 700, color: TEAL, marginLeft: '6px' }}>
                      {heatData[z.id]}×
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Zone selected: emotion + intensity picker ── */}
        {selectedZone && (
          <div style={{
            background: CREAM, border: '1px solid rgba(30,107,104,0.12)',
            borderRadius: '20px', padding: '24px', marginBottom: '20px',
            boxShadow: '0 2px 16px rgba(30,107,104,0.07)',
            animation: 'fadeSlideIn 0.25s ease',
          }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 700, color: '#1A2E2C' }}>
              {zoneName}
            </h3>
            <p style={{ margin: '0 0 18px', fontSize: '13px', color: '#7A9B97' }}>
              What are you feeling here right now?
            </p>

            {/* Emotion grid */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
              {EMOTIONS.map(em => (
                <button key={em.id} onClick={() => setSelectedEmotion(em.id)} style={{
                  padding: '8px 14px', borderRadius: '20px',
                  background: selectedEmotion === em.id ? em.color : 'rgba(0,0,0,0.04)',
                  color: selectedEmotion === em.id ? '#fff' : '#4A6A66',
                  border: `1.5px solid ${selectedEmotion === em.id ? em.color : 'rgba(0,0,0,0.08)'}`,
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  transition: 'all 0.15s',
                }}>
                  {em.emoji} {em.label}
                </button>
              ))}
            </div>

            {/* Intensity slider */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#6B8B87' }}>INTENSITY</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color: DARK }}>{intensity}/10</span>
              </div>
              <input type="range" min="1" max="10" value={intensity}
                onChange={e => setIntensity(Number(e.target.value))}
                style={{ width: '100%', accentColor: TEAL }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                <span style={{ fontSize: '10px', color: '#A0B4B0' }}>Barely there</span>
                <span style={{ fontSize: '10px', color: '#A0B4B0' }}>Very strong</span>
              </div>
            </div>

            {/* Optional note */}
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Any thoughts? (optional)"
              rows={2}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1.5px solid rgba(30,107,104,0.15)',
                borderRadius: '10px', fontSize: '13px', lineHeight: 1.6,
                background: 'rgba(255,255,255,0.8)', color: '#1E3A39',
                resize: 'none', fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box', marginBottom: '16px',
              }}
            />

            <button
              onClick={handleSave}
              disabled={!selectedEmotion || saving}
              style={{
                width: '100%', padding: '13px',
                background: selectedEmotion && !saving ? `linear-gradient(135deg, ${DARK}, ${TEAL})` : 'rgba(0,0,0,0.08)',
                color: selectedEmotion && !saving ? '#fff' : '#A0A0A0',
                border: 'none', borderRadius: '14px',
                fontSize: '14px', fontWeight: 600,
                cursor: selectedEmotion && !saving ? 'pointer' : 'not-allowed',
                boxShadow: selectedEmotion ? '0 4px 14px rgba(30,107,104,0.25)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              {saving ? 'Saving…' : 'Save this check-in'}
            </button>
          </div>
        )}

        {/* Saved confirmation */}
        {saved && (
          <div style={{
            background: 'rgba(30,107,104,0.08)', border: '1px solid rgba(30,107,104,0.20)',
            borderRadius: '14px', padding: '14px 18px', marginBottom: '20px',
            fontSize: '14px', color: DARK, fontWeight: 500,
          }}>
            ✓ Recorded. Your somatic map is updating.
          </div>
        )}

        {/* ── Analytics: top zones ── */}
        {topZones.length > 0 && (
          <div style={{
            background: CREAM, border: '1px solid rgba(30,107,104,0.10)',
            borderRadius: '20px', padding: '22px', marginBottom: '20px',
          }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: 700,
              color: '#6B8B87', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Your activation pattern (last 60 sessions)
            </h3>
            {topZones.map(({ zone, count, label }) => (
              <div key={zone} style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#1A2E2C' }}>{label}</span>
                  <span style={{ fontSize: '12px', color: TEAL, fontWeight: 700 }}>{count}×</span>
                </div>
                <div style={{ height: '5px', background: 'rgba(0,0,0,0.06)', borderRadius: '3px' }}>
                  <div style={{
                    height: '100%', borderRadius: '3px',
                    width: `${(count / maxCount) * 100}%`,
                    background: `linear-gradient(90deg, ${DARK}, ${TEAL})`,
                    transition: 'width 0.8s ease',
                  }} />
                </div>
              </div>
            ))}
            {topZones.length > 0 && (
              <p style={{ margin: '14px 0 0', fontSize: '12px', color: '#8B9B97', lineHeight: 1.5 }}>
                Patterns in your body are data, not destiny. Frequent activation in an area simply means the nervous system has learned to hold something there.
              </p>
            )}
          </div>
        )}

        {/* ── Recent check-ins ── */}
        {recent.length > 0 && (
          <div>
            <h3 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 700,
              color: '#6B8B87', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Recent check-ins
            </h3>
            {recent.map((h, i) => {
              const zone   = h.body_zone || h.zone;
              const emo    = h.emotion;
              const zLabel = ZONES.find(z => z.id === zone)?.label || zone;
              const eObj   = EMOTIONS.find(e => e.id === emo);
              const dt     = new Date(h.created_at || h.timestamp);
              return (
                <div key={i} style={{
                  background: CREAM, border: '1px solid rgba(30,107,104,0.08)',
                  borderRadius: '12px', padding: '12px 16px', marginBottom: '8px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1A2E2C' }}>{zLabel}</span>
                    {eObj && (
                      <span style={{
                        marginLeft: '8px', fontSize: '12px', fontWeight: 600,
                        color: eObj.color, background: `${eObj.color}12`,
                        padding: '2px 8px', borderRadius: '10px',
                      }}>{eObj.emoji} {eObj.label}</span>
                    )}
                    {h.intensity && (
                      <span style={{ marginLeft: '6px', fontSize: '11px', color: '#8B9B97' }}>
                        intensity {h.intensity}/10
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '11px', color: '#A0B4B0', flexShrink: 0, marginLeft: '12px' }}>
                    {isNaN(dt) ? '' : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {!loading && history.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#8B9B97' }}>
            <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'center' }}>
              <BodySenseIcon size={44} color="#AAB8B6" />
            </div>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6 }}>
              No check-ins yet. Tap a body zone above to begin your somatic map.
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
